// src/components/sdk/CreateAppModal.tsx
import React from 'react';
import { Modal, Form, Input, Select, InputNumber, DatePicker, Switch } from 'antd';
import { withTranslation } from 'react-i18next';
import dayjs from 'dayjs';

interface CreateAppModalProps {
    visible: boolean;
    loading: boolean;
    onOk: () => void;
    onCancel: () => void;
    t: (key: string, options?: any) => string;
    formRef: React.RefObject<any>;
    isMobile?: boolean;
}

export class CreateAppModal extends React.Component<CreateAppModalProps> {
    render() {
        const { visible, loading, onOk, onCancel, t, formRef, isMobile = false } = this.props;

        // 移动端样式
        const modalStyle: React.CSSProperties = isMobile ? {
            top: 0,
            maxWidth: '100%',
            margin: 0,
            padding: 0,
        } : {};

        const modalWidth = isMobile ? '100%' : 520;

        const bodyStyle: React.CSSProperties = isMobile ? {
            padding: '12px 16px',
            maxHeight: 'calc(100vh - 120px)',
            overflowY: 'auto',
            height: 'calc(100vh - 120px)',
        } : {};

        return (
            <Modal
                title={t('sdk.createModal.title') || '创建新应用'}
                open={visible}
                onOk={onOk}
                onCancel={onCancel}
                confirmLoading={loading}
                okText={t('sdk.createModal.ok') || '创建'}
                cancelText={t('sdk.createModal.cancel') || '取消'}
                width={modalWidth}
                style={modalStyle}
                bodyStyle={bodyStyle}
                maskClosable={false}
                closable={true}
                destroyOnClose
            >
                <Form
                    ref={formRef}
                    layout="vertical"
                    initialValues={{
                        platform: 'android',
                        initialQuota: 0,
                        unlimited: false,
                    }}
                >
                    <Form.Item
                        name="appName"
                        label={t('sdk.form.appName') || '应用名称'}
                        rules={[
                            { required: true, message: t('sdk.validation.appNameRequired') || '请输入应用名称' },
                            { max: 50, message: t('sdk.validation.appNameMax') || '应用名称不能超过50个字符' },
                        ]}
                    >
                        <Input
                            placeholder={t('sdk.form.appNamePlaceholder') || '请输入应用名称，如：我的应用'}
                            size={isMobile ? 'middle' : 'middle'}
                        />
                    </Form.Item>

                    <Form.Item
                        name="packageName"
                        label={t('sdk.form.packageName') || '包名'}
                        rules={[
                            { required: true, message: t('sdk.validation.packageNameRequired') || '请输入包名' },
                            {
                                pattern: /^[a-zA-Z][a-zA-Z0-9_]*(?:\.[a-zA-Z][a-zA-Z0-9_]*)+$/,
                                message: t('sdk.validation.packageNameInvalid') || '请输入有效的包名格式，如：com.example.app',
                            },
                        ]}
                    // extra={t('sdk.form.packageNameExtra') || '包名格式：com.公司名.应用名，只能包含字母、数字和下划线'}
                    >
                        <Input
                            placeholder={t('sdk.form.packageNamePlaceholder') || '请输入包名，如：com.example.myapp'}
                            size={isMobile ? 'middle' : 'middle'}
                        />
                    </Form.Item>

                    <Form.Item
                        name="platform"
                        label={t('sdk.form.platform') || '平台类型'}
                        rules={[{ required: true, message: t('sdk.validation.platformRequired') || '请选择平台类型' }]}
                    >
                        <Select
                            placeholder={t('sdk.form.platformPlaceholder') || '请选择平台'}
                            size={isMobile ? 'middle' : 'middle'}
                            options={[
                                { value: 'android', label: t('sdk.platform.android') || 'Android' },
                                { value: 'ios', label: t('sdk.platform.ios') || 'iOS' },
                            ]}
                        />
                    </Form.Item>

                    {/* 初始可用次数 + 无限制开关 */}
                    <Form.Item
                        label={t('sdk.form.initialQuota') || '初始可用次数'}
                    // extra={t('sdk.form.initialQuotaExtra') || '应用创建时分配的初始调用次数，开启无限制则不限制调用次数'}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Form.Item
                                name="initialQuota"
                                noStyle
                                rules={[
                                    { required: true, message: t('sdk.validation.initialQuotaRequired') || '请输入初始可用次数' },
                                    { type: 'number', min: 0, message: t('sdk.validation.initialQuotaMin') || '初始可用次数不能小于0' },
                                ]}
                            >
                                <InputNumber
                                    style={{ flex: 1 }}
                                    placeholder={t('sdk.form.initialQuotaPlaceholder') || '请输入初始可用次数'}
                                    min={0}
                                    precision={0}
                                    size={isMobile ? 'middle' : 'middle'}
                                />
                            </Form.Item>
                            <Form.Item
                                name="unlimited"
                                noStyle
                                valuePropName="checked"
                            >
                                <Switch
                                    checkedChildren={t('sdk.form.unlimited') || '无限制'}
                                    unCheckedChildren={t('sdk.form.limited') || '有限'}
                                    size={isMobile ? 'default' : 'default'}
                                />
                            </Form.Item>
                        </div>
                    </Form.Item>

                    {/* 监听 unlimited 字段来控制初始可用次数的禁用状态 */}
                    <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue, setFieldsValue }) => {
                            const unlimited = getFieldValue('unlimited');
                            if (unlimited) {
                                const currentQuota = getFieldValue('initialQuota');
                                if (currentQuota !== 0) {
                                    setFieldsValue({ initialQuota: 0 });
                                }
                            }
                            return null;
                        }}
                    </Form.Item>

                    {/* 过期时间 */}
                    <Form.Item
                        name="expireTime"
                        label={t('sdk.form.expireTime') || '过期时间'}
                        rules={[
                            { required: true, message: t('sdk.validation.expireTimeRequired') || '请选择过期时间' },
                        ]}
                    // extra={t('sdk.form.expireTimeExtra') || '应用到期后将无法继续使用，请合理设置有效期'}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            placeholder={t('sdk.form.expireTimePlaceholder') || '请选择过期时间'}
                            showTime={{ format: 'HH:mm:ss' }}
                            format="YYYY-MM-DD HH:mm:ss"
                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                            size={isMobile ? 'middle' : 'middle'}
                        />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={t('sdk.form.description') || '应用描述'}
                        rules={[
                            { max: 200, message: t('sdk.validation.descriptionMax') || '描述不能超过200个字符' },
                        ]}
                    >
                        <Input.TextArea
                            placeholder={t('sdk.form.descriptionPlaceholder') || '请输入应用描述（可选）'}
                            rows={4}
                            showCount
                            maxLength={200}
                            style={isMobile ? { fontSize: 15 } : {}}
                        />
                    </Form.Item>

                    {/* 显示初始可用次数的禁用状态 */}
                    <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                            const unlimited = getFieldValue('unlimited');
                            const form = formRef.current;
                            if (form) {
                                const instance = form.getFieldInstance('initialQuota');
                                if (instance && instance.input) {
                                    const inputElement = instance.input;
                                    if (inputElement) {
                                        inputElement.disabled = unlimited;
                                    }
                                }
                            }
                            return null;
                        }}
                    </Form.Item>
                </Form>
            </Modal>
        );
    }
}

// export default withTranslation()(CreateAppModal);